/**
 * Shared helpers for the supplier-quote sharing flow.
 */
import { supabase } from '@/integrations/supabase/client';

export const DEFAULT_SHARE_MESSAGE_TEMPLATE = [
  'Olá, {nome}! É um prazer dar continuidade ao planejamento do seu evento.',
  '',
  'Preparei a proposta focando na variedade e qualidade que conversamos. Se precisar de qualquer ajuste no cardápio, é só me chamar!',
  '',
  '{link}',
  '',
  'Para seguir com a reserva, basta seguir com o pagamento e assinatura no mesmo link. Assim que o processo for concluído, nossa equipe já inicia os preparativos para o seu grande dia.',
].join('\n');

const applyTemplate = (template: string, clientName: string, shareUrl: string): string => {
  const safeName = clientName?.trim() || 'Cliente';
  let result = template.replace(/\{nome\}/gi, safeName).replace(/\{link\}/gi, shareUrl);
  // If template doesn't include {link}, append URL on a new line so the link is always shared.
  if (!/\{link\}/i.test(template) && !result.includes(shareUrl)) {
    result = `${result}\n\n${shareUrl}`;
  }
  return result;
};

export const buildQuoteShareMessage = (
  clientName: string,
  shareUrl: string,
  template?: string | null,
): string => {
  return applyTemplate(template?.trim() ? template : DEFAULT_SHARE_MESSAGE_TEMPLATE, clientName, shareUrl);
};

/**
 * Fetches the supplier's custom share message template by slug.
 * Falls back to the default template when none is configured.
 */
export const fetchSupplierShareTemplate = async (slug?: string | null): Promise<string> => {
  if (!slug) return DEFAULT_SHARE_MESSAGE_TEMPLATE;
  try {
    const { data: supplier } = await supabase
      .from('suppliers_public')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    if (!supplier?.id) return DEFAULT_SHARE_MESSAGE_TEMPLATE;

    const { data: cust } = await supabase
      .from('supplier_store_customization')
      .select('share_message')
      .eq('supplier_id', supplier.id)
      .maybeSingle();

    const tpl = (cust as any)?.share_message?.trim();
    return tpl || DEFAULT_SHARE_MESSAGE_TEMPLATE;
  } catch {
    return DEFAULT_SHARE_MESSAGE_TEMPLATE;
  }
};

/**
 * Detects whether the current page was opened from a shared quote link.
 */
export const isSharedQuoteContext = (search?: string): boolean => {
  try {
    const raw = typeof search === 'string'
      ? search
      : (typeof window !== 'undefined' ? window.location.search : '');
    if (!raw) return false;
    const params = new URLSearchParams(raw);
    return !!(
      params.get('ref') ||
      params.get('pid') ||
      params.get('items') ||
      params.get('nome') ||
      params.get('mode') === 'orcamento'
    );
  } catch {
    return false;
  }
};

/**
 * Checks if a shared quote link is expired (validity: 30 days).
 */
export const isLinkExpired = (createdAt?: string | Date | null): boolean => {
  if (!createdAt) return false;
  try {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffInMs = now.getTime() - createdDate.getTime();
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    return diffInMs > thirtyDaysInMs;
  } catch {
    return false;
  }
};
